import { useMemo } from "react"

import { useAuth } from "shared/lib/useAuth"

import { TeamRole, isTeamRole } from "entities/team-members/types/TeamRole"

import { TeamInvitationFragment } from "../graphql/fragments.generated"
import { useTeamInvitationsQuery } from "../graphql/queries.generated"
import { TeamInvitation } from "../types/TeamInvitation"
import { TeamInvitationStatus } from "../types/TeamInvitationStatus"

interface UseTeamInvitationsDataArgs {
  pending?: boolean
}

export function useTeamInvitationListData(args?: UseTeamInvitationsDataArgs) {
  const { isAuthenticated } = useAuth()
  const { pending = true } = args || {}
  const {
    data,
    loading: isLoading,
    error
  } = useTeamInvitationsQuery({
    variables: { pending },
    skip: !isAuthenticated
  })

  const teamInvitations = useMemo<TeamInvitation[]>(() => {
    return (
      data?.teamInvitations?.map(invitation =>
        transformTeamInvitation(invitation)
      ) || []
    )
  }, [data?.teamInvitations])

  return {
    teamInvitations,
    isLoading,
    error
  }
}

function transformTeamInvitation(
  invitation: TeamInvitationFragment
): TeamInvitation {
  return {
    ...invitation,
    id: invitation.id,
    status: getStatus(invitation),
    role: isTeamRole(invitation.role) ? invitation.role : TeamRole.READ,
    acceptInvitationPath: invitation.acceptInvitationPath
  }
}

function getStatus(invitation: TeamInvitationFragment) {
  if (invitation.invitationAccepted) {
    return TeamInvitationStatus.ACCEPTED
  }
  if (invitation.invitationRejected) {
    return TeamInvitationStatus.DECLINED
  }
  return TeamInvitationStatus.PENDING
}
